import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type GetQuoteResult } from '@whinerp/client';
import { formatDate } from '@whinerp/contracts/format';
import { Button, Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';

/**
 * A quote's terms and the date it is valid until (F-048, F-158).
 *
 * Both were printed and shown to the customer, and nothing on screen could
 * change them. A template fills them in, and the UX pointer for templates is
 * that everything must remain editable afterwards - so this is the editor a
 * template's terms land in.
 */
export function QuoteTermsPanel({ quote }: { quote: GetQuoteResult }): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const [terms, setTerms] = useState(quote.terms ?? '');
  const [validUntil, setValidUntil] = useState(quote.validUntil ?? '');

  useEffect(() => {
    setTerms(quote.terms ?? '');
    setValidUntil(quote.validUntil ?? '');
  }, [quote.terms, quote.validUntil]);

  const save = useMutation<GetQuoteResult, ApiError>({
    mutationFn: () =>
      api.setQuoteTerms({
        path: { quoteId: quote.quoteId },
        body: { terms: terms.trim() === '' ? null : terms, validUntil: validUntil === '' ? null : validUntil },
      }),
    onSuccess: (saved) => queryClient.setQueryData(['quote', quote.quoteId], saved),
  });

  const changed = terms !== (quote.terms ?? '') || validUntil !== (quote.validUntil ?? '');

  return (
    <Panel data-region="quote-terms">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('quote.termsTitle')}</SectionLabel>
        {save.error ? (
          <p data-quote-terms-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">
            {errorText(save.error)}
          </p>
        ) : null}
        {quote.editable ? (
          <>
            <label className="flex flex-col gap-1 text-dense text-ink-600">
              {t('quote.validUntil')}
              <input
                data-quote-valid-until-input
                type="date"
                className="h-input w-panel-narrow rounded-control border border-control bg-surface px-3 text-body text-ink"
                value={validUntil}
                onChange={(event) => setValidUntil(event.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1 text-dense text-ink-600">
              {t('quote.terms')}
              <textarea
                data-quote-terms-input
                rows={5}
                className="rounded-control border border-control bg-surface px-3 py-2 text-body text-ink"
                value={terms}
                onChange={(event) => setTerms(event.target.value)}
              />
            </label>
            <div>
              <Button
                intent="secondary"
                size="small"
                data-quote-terms-save
                disabled={!changed || save.isPending}
                onClick={() => save.mutate()}
              >
                {t('quote.termsSave')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-dense text-ink">
              {quote.validUntil
                ? `${t('quote.validUntil')}: ${formatDate(quote.validUntil, format)}`
                : t('quote.noValidity')}
            </p>
            <p data-quote-terms-readonly className="whitespace-pre-wrap text-body text-ink">
              {quote.terms ?? t('quote.noTerms')}
            </p>
          </>
        )}
      </div>
    </Panel>
  );
}
